"use client";

import { c as _c } from "react/compiler-runtime";
import { MEMES_MANIFOLD_PROXY_ABI } from "@/abis";
import { wallTimeToUtcInstantInZone } from "@/components/meme-calendar/meme-calendar.helpers";
import { MANIFOLD_NETWORK, MEMES_CONTRACT, MEMES_MANIFOLD_PROXY_CONTRACT, NULL_MERKLE } from "@/constants";
import { areEqualAddresses } from "@/helpers/Helpers";
import { Time } from "@/helpers/time";
import { useCallback, useEffect, useState } from "react";
import type { Abi } from "viem";
import { useReadContract } from "wagmi";
export enum ManifoldClaimStatus {
  UPCOMING = "upcoming",
  ACTIVE = "active",
  ENDED = "ended",
}
export enum ManifoldPhase {
  ALLOWLIST = "Allowlist",
  PUBLIC = "Public Phase",
}
interface PhaseBoundary {
  hour: number;
  minute: number;
  dayOffset?: number;
}
interface PhaseDefinition {
  id: string;
  name: string;
  type: ManifoldPhase;
  start: PhaseBoundary;
  end: PhaseBoundary;
}
export interface MemePhase {
  id: string;
  name: string;
  type: ManifoldPhase;
  start: Time;
  end: Time;
}
const PHASE_DEFINITIONS: PhaseDefinition[] = [{
  id: "0",
  name: "Phase 0 (Allowlist)",
  type: ManifoldPhase.ALLOWLIST,
  start: {
    hour: 17,
    minute: 40
  },
  end: {
    hour: 18,
    minute: 20
  }
}, {
  id: "1",
  name: "Phase 1 (Allowlist)",
  type: ManifoldPhase.ALLOWLIST,
  start: {
    hour: 18,
    minute: 30
  },
  end: {
    hour: 18,
    minute: 50
  }
}, {
  id: "2",
  name: "Phase 2 (Allowlist)",
  type: ManifoldPhase.ALLOWLIST,
  start: {
    hour: 19,
    minute: 0
  },
  end: {
    hour: 19,
    minute: 20
  }
}, {
  id: "public",
  name: "Public Phase",
  type: ManifoldPhase.PUBLIC,
  start: {
    hour: 19,
    minute: 20
  },
  end: {
    hour: 17,
    minute: 0,
    dayOffset: 1
  }
}];
export function buildMemesPhases(mintDate: Time = Time.now()): MemePhase[] {
  const resolveTime = ({
    hour,
    minute,
    dayOffset = 0
  }: PhaseBoundary): Time => {
    const reference = dayOffset === 0 ? mintDate : mintDate.plusDays(dayOffset);
    const instant = wallTimeToUtcInstantInZone(reference.toDate(), hour, minute);
    return Time.millis(instant.getTime());
  };
  return PHASE_DEFINITIONS.map(({
    start,
    end,
    ...phase
  }) => ({
    ...phase,
    start: resolveTime(start),
    end: resolveTime(end)
  }));
}
export interface ManifoldClaim {
  instanceId: number;
  total: number;
  totalMax: number;
  remaining: number;
  cost: number;
  startDate: number;
  endDate: number;
  status: ManifoldClaimStatus;
  phase: ManifoldPhase;
  memePhase?: MemePhase;
  isFetching: boolean;
  isFinalized: boolean;
  isSoldOut: boolean;
  isError: boolean;
}
export function useManifoldClaim(contract, proxy, abi, tokenId, onError) {
  const $ = _c(25);
  const [claim, setClaim] = useState();
  const [refetchInterval, setRefetchInterval] = useState(5000);
  const getStatus = _temp;
  let t0;
  if ($[0] !== contract) {
    t0 = (phase, start_0, end_0) => {
      if (!areEqualAddresses(contract, MEMES_CONTRACT)) {
        return;
      }
      const memePhases = buildMemesPhases(Time.seconds(start_0));
      if (phase === ManifoldPhase.PUBLIC) {
        return memePhases.find(_temp2);
      }
      const endTime = Time.seconds(end_0);
      return memePhases.find(mp_0 => mp_0.end.gte(endTime));
    };
    $[0] = contract;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getMemePhase = t0;
  const t1 = proxy as `0x${string}`;
  const t2 = !!contract && !!proxy && !!abi && tokenId >= 0 && !claim?.isFinalized;
  let t3;
  if ($[2] !== refetchInterval || $[3] !== t2) {
    t3 = {
      enabled: t2,
      refetchInterval
    };
    $[2] = refetchInterval;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== contract || $[6] !== tokenId) {
    t4 = [contract, tokenId];
    $[5] = contract;
    $[6] = tokenId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== abi || $[9] !== t1 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      address: t1,
      abi,
      query: t3,
      chainId: MANIFOLD_NETWORK.id,
      functionName: "getClaimForToken",
      args: t4
    };
    $[8] = abi;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const readContract = useReadContract(t5);
  let t6;
  if ($[13] !== getMemePhase || $[14] !== readContract.data) {
    t6 = () => {
      if (readContract.data) {
        const data = readContract.data as any;
        const instanceId = Number(data[0]);
        const claimData = data[1];
        const status = getStatus(claimData.startDate, claimData.endDate);
        const publicMerkle = areEqualAddresses(NULL_MERKLE, claimData.merkleRoot);
        const phase_0 = publicMerkle && claimData.total > 0 ? ManifoldPhase.PUBLIC : ManifoldPhase.ALLOWLIST;
        const memePhase = getMemePhase(phase_0, claimData.startDate, claimData.endDate);
        const remaining = Number(claimData.totalMax) - Number(claimData.total);
        const newClaim = {
          instanceId,
          total: Number(claimData.total),
          totalMax: Number(claimData.totalMax),
          remaining,
          cost: Number(claimData.cost),
          startDate: Number(claimData.startDate),
          endDate: Number(claimData.endDate),
          status,
          phase: phase_0,
          memePhase,
          isFetching: false,
          isFinalized: remaining === 0 || status === ManifoldClaimStatus.ENDED,
          isSoldOut: remaining <= 0,
          isError: false
        };
        setClaim(newClaim);
        setRefetchInterval(status === ManifoldClaimStatus.ACTIVE ? 5000 : 10000);
      }
    };
    $[13] = getMemePhase;
    $[14] = readContract.data;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== readContract.data) {
    t7 = [readContract.data, getStatus];
    $[16] = readContract.data;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[18] !== onError || $[19] !== readContract.error) {
    t8 = () => {
      if (readContract.error) {
        setClaim(_temp3);
        onError?.();
      }
    };
    t9 = [readContract.error, onError];
    $[18] = onError;
    $[19] = readContract.error;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[22] !== readContract.isFetching) {
    t10 = () => {
      setClaim(prevClaim_0 => {
        if (prevClaim_0) {
          return {
            ...prevClaim_0,
            isFetching: readContract.isFetching
          };
        }
      });
    };
    t11 = [readContract.isFetching];
    $[22] = readContract.isFetching;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  return claim;
}
function _temp3(prevClaim) {
  if (prevClaim) {
    return {
      ...prevClaim,
      isError: true
    };
  }
}
function _temp2(mp) {
  return mp.id === "public";
}
function _temp(start, end) {
  const now = Time.now().toSeconds();
  if (now < start) {
    return ManifoldClaimStatus.UPCOMING;
  } else {
    if (now >= start && now < end) {
      return ManifoldClaimStatus.ACTIVE;
    }
  }
  return ManifoldClaimStatus.ENDED;
}
export function useMemesManifoldClaim(tokenId, onError) {
  return useManifoldClaim(MEMES_CONTRACT, MEMES_MANIFOLD_PROXY_CONTRACT, MEMES_MANIFOLD_PROXY_ABI, tokenId, onError);
}